import { useState } from "react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { PageContainer } from "@/components/ui/page-container"
import { PageHeader } from "@/components/ui/page-header"
import { PageHeaderLeading } from "@/components/navigation/page-header-leading"
import { authorizedFetch } from "@/lib/api"
import { API_ORIGIN } from "@/constants"
import { Mail } from "lucide-react"

import { getPlatformAssignableGroups } from '@/lib/platform-assignable-groups'

interface UserInviteFormProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
  platformMode?: boolean;
}

export function UserInviteForm({ className, onSuccess, platformMode = false, ...props }: UserInviteFormProps) {
  const [email, setEmail] = useState("")
  const [groupNames, setGroupNames] = useState<string[]>([])
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [invitationKey, setInvitationKey] = useState("")
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess(false)

    if (!email) {
      setError("Please enter an email address")
      return
    }

    setIsLoading(true)

    try {
      const assignable = new Set(getPlatformAssignableGroups().map((group) => group.name))
      const roleGroups = groupNames.filter((name) => assignable.has(name))
      const response = await authorizedFetch(`${API_ORIGIN}/api/accounts/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          group_names: roleGroups.length > 0 ? roleGroups : undefined
        }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
        setInvitationKey(data.invitation_key)
        setEmail("")
        setGroupNames([])
        onSuccess?.()
      } else {
        setError(data.error || 'Failed to send invitation')
      }
    } catch (error) {
      console.error('Invitation error:', error)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const assignableGroupHint = platformMode
    ? getPlatformAssignableGroups().map((g) => g.name).join(', ') || 'candidate, employer, recruiter'
    : 'candidate, employer, recruiter'

  return (
    <PageContainer maxWidth="2xl" className={cn("flex flex-col gap-6", className)} {...props}>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Invite user"
        description="Send an invitation to create a new user account"
        icon={<Mail className="h-5 w-5" />}
      />
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          {error && (
            <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
              Invitation sent successfully! Invitation key: {invitationKey}
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="groups">Groups (optional, comma-separated)</Label>
            <Input
              id="groups"
              type="text"
              placeholder={assignableGroupHint}
              value={groupNames.join(', ')}
              onChange={(e) => {
                const groups = e.target.value.split(',').map(g => g.trim()).filter(g => g.length > 0)
                setGroupNames(groups)
              }}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Optional: assign front-facing roles when they accept ({assignableGroupHint})
            </p>
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? "Sending..." : "Send invitation"}
          </Button>
        </div>
      </form>
    </PageContainer>
  )
}
